import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ScanSearch, BookOpen, Sparkles, ListChecks } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import AnalyzeBox from "@/components/AnalyzeBox";
import LeanGauge from "@/components/LeanGauge";
import NewsTicker from "@/components/NewsTicker";
import Stepper, { Step } from "@/components/reactbits/Stepper";
import StrokeText from "@/components/reactbits/StrokeText";
import RotatingText from "@/components/reactbits/RotatingText";
import SpotlightCard from "@/components/reactbits/SpotlightCard";
import PixelCard, { pixelInk } from "@/components/reactbits/PixelCard";
import { getRecent, getReport, leanStyle } from "@/lib/api";
import { EASE_OUT } from "@/lib/motion";
import { useTheme } from "@/lib/theme";
import { usePageMeta } from "@/lib/seo";

const HERO_BG = "https://images.unsplash.com/photo-1698778539388-fd440b64dbe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5ld3NwYXBlciUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzg2NTcxODg1fDA&ixlib=rb-4.1.0&q=85";
// The same photograph, asked for at a phone's width and a lower quality. The
// trailing q wins over the one already in the string — Unsplash's imgix takes
// the last of a repeated parameter — which is the point: this is a 6% wash
// behind the headline, so it can afford neither the pixels nor the bitrate of
// the full-size file on a connection that is paying for both. Only the mobile
// layer requests it; the desktop layer still asks for HERO_BG unchanged.
const HERO_BG_SM = `${HERO_BG}&w=760&q=45`;

// The one telling of the four stages, rendered as cards at md and up and as the
// stepper below it. It used to be three cards here and a separate hardcoded
// four in the stepper, so the same section said different things depending on
// the width of the screen — and the copy still described the machinery it was
// rewritten to stop describing.
const STEPS = [
  { icon: ScanSearch, title: "Paste", body: "Drop in a link to any news story. That is the whole of your side of it — no account, no setup, nothing to install." },
  { icon: BookOpen, title: "Read", body: "We open the page and lift out the article itself — headline, author, date and the story they told. Menus, adverts and comments are left behind." },
  { icon: Sparkles, title: "Weigh", body: "Then the reading proper: the words the piece chooses, who it lets speak, and whose view never turns up at all. That becomes your slant score." },
  { icon: ListChecks, title: "Share", body: "You get a page of your own to keep or send on, and it stays put. Ask for the same story again and the reading is already there waiting." },
];

const FAQ = [
  { q: "Is this accurate?", a: "SlantScan uses a large language model to estimate slant and framing. It's a well-informed opinion, not a fact-check — treat scores as a starting point for critical reading, not the final word." },
  { q: "How is slant scored?", a: "We send the extracted article to an AI model that returns a score from -10 (strongly left) to +10 (strongly right), based on word choice, sourcing, framing, and which perspectives are emphasized or omitted." },
  { q: "Is it free?", a: "Yes. Run 2 scans with no account, then create a free account for 5 scans a day. Pro unlocks unlimited scans, every highlight and compare mode." },
  { q: "Do you store articles?", a: "We store the extracted text and analysis so reports have permanent shareable links. Shared report links are always viewable by anyone, no login required." },
];

export default function Landing() {
  const reduceMotion = useReducedMotion();
  // Canvas takes concrete colours, so this one can't be a CSS variable.
  const isDark = useTheme() === "dark";
  const [recent, setRecent] = useState([]);
  const [demo, setDemo] = useState(null);

  usePageMeta(
    "SlantScan — See any news article's slant in 10 seconds",
    "Paste any live news URL. SlantScan scores its political slant, highlights loaded language inline, and shows the perspectives the article left out.",
  );

  useEffect(() => {
    getRecent(7).then(async (list) => {
      setRecent(list);
      const pick = list.find((r) => Math.abs(r.lean_score) >= 3) || list[0];
      if (pick) { try { setDemo(await getReport(pick.id)); } catch { /* ignore */ } }
    }).catch(() => {});
  }, []);

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line/10">
        {/* Two layers rather than one with a media query, because the source is
            an inline style and a media query cannot reach into one. Exactly one
            is ever displayed, and a browser does not fetch the background of a
            `display: none` element, so each viewport downloads one file. */}
        <div className="absolute inset-0 opacity-[0.06] sm:hidden" style={{ backgroundImage: `url(${HERO_BG_SM})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 hidden opacity-[0.06] sm:block" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        {/* py-20 was two thirds of a phone's viewport spent on empty space
            above and below the one thing this page asks you to do. */}
        <div className="relative mx-auto max-w-4xl px-5 py-14 sm:py-28">
          {/* The container the headline's cqw sizing resolves against. It has to
              be an ancestor — an element is never its own query container. */}
          <motion.div className="[container-type:inline-size]"
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE_OUT }}>
            <span className="inline-block rounded-full border border-line/10 bg-surface px-3 py-1 text-xs font-semibold uppercase tracking-widest text-faint">
              Bias scorer for live news
            </span>
            {/* Two block-level lines rather than a <br>, so each can carry its
                own treatment. Asked for less motion the headline simply arrives:
                this is the largest movement on the page and it plays unprompted
                on first paint, which is exactly what the preference is about.

                The drawn line cannot wrap — SVG text is one line at whatever
                size fits the width — so it scales continuously while Tailwind's
                text-4xl/5xl/6xl steps in jumps. Left alone the two lines swap
                which one is bigger: measured 83px against 60px at 1280, and 33px
                against 36px at 375. So the whole headline goes fluid off one
                container width instead.

                9.75cqw is the drawn line's own scaling law, not a taste call:
                it renders at fontSize 128 inside a viewBox 1312.77 wide, and
                128/1312.77 is 9.75% of whatever width it is given. Line two
                matching that keeps them the same size at every viewport. Retune
                it if the first line's wording changes, because the viewBox width
                is a measurement of that specific string. */}
            <h1 className="mt-5 font-serif text-[9.75cqw] font-black leading-[1.05] tracking-tight text-ink">
              {reduceMotion ? (
                // The reduced-motion headline is the same headline, so it gets
                // the same treatment on the same word — the fill is colour,
                // not movement, and nothing about it is what the preference is
                // asking to be spared.
                <>Paste any news article.<br />See its <span className="lean-axis-text">slant</span> in 10 seconds.</>
              ) : (
                <>
                  {/* Drawn rather than blurred in. StrokeText REPLACES BlurText
                      here rather than joining it: two entrance animations on one
                      headline read as fidgeting, which is the same reason line
                      two below was left plain.

                      currentColor for both paints, not a hex and not
                      `rgb(var(--c-ink))` — these land on SVG presentation
                      attributes, where a CSS variable is not reliably resolved.
                      currentColor inherits `text-ink` off the wrapper and so
                      follows the theme for free.

                      Deliberately slow, and slower than this file used to argue
                      for. A 0.12 stagger across 23 characters spreads their
                      starts over 2.64s, and the last of them draws for 3s and
                      lands at 5.64s. The fill sweeps across from 3.2s to 4.7s,
                      overlapping the back half of that rather than following it,
                      so the word is fully set at about 5.6s against the ten
                      seconds this product promises for a whole analysis. The
                      draw is the thing worth watching here; the stagger is the
                      cheapest number to pull back if the hero ever needs to get
                      out of the way, since it halves the spread without making
                      any single letter draw faster. Anyone who asked for reduced
                      motion still gets it fully drawn on the first frame. */}
                  <StrokeText
                    text="Paste any news article."
                    className="font-serif text-ink"
                    strokeColor="currentColor"
                    fillColor="currentColor"
                    strokeWidth={2}
                    fontWeight={900}
                    letterSpacing={-3}
                    // 640 is a shade above the longest outline a Playfair Black
                    // glyph draws at this size, so the travel is nearly all
                    // reveal instead of the default 896, which spent its first
                    // 40% drawing nothing.
                    drawLength={640}
                    drawDuration={3}
                    stagger={0.12}
                    // Short on purpose, so the fill runs concurrently with the
                    // draw rather than waiting behind it.
                    //
                    // Worth knowing what that means, because the wipe is placed
                    // at drawDuration + fillDelay — an absolute point that knows
                    // nothing about how far the stagger has carried the last
                    // character. The wipe starts at 3.2s and crosses the word in
                    // 1.5s; character i is not fully drawn until 0.12i + 3. Those
                    // cross at i≈4, so from roughly the fourth letter onward the
                    // fill arrives before the outline does, and by the final
                    // character it is a full second early. Clearing the stagger
                    // instead would need about 2.8 here.
                    fillDelay={0.2}
                    fillMode="wipe"
                    // sine.inOut: near-linear through the middle, softened at
                    // both ends. The middle is what matters here — an `out`
                    // curve front-loads the offset and lands the reveal well
                    // before the duration is up, which is what made the draw
                    // look like it was skipped.
                    ease="sine.inOut"
                  />
                  {/* Line two isn't BlurText: it carries the rotating noun, and
                      two entrance animations on one headline read as fidgeting.
                      Every word here is something the scorer actually measures,
                      so the rotation restates the product rather than decorating
                      it. */}
                  <span className="flex flex-wrap items-center">
                    See its&nbsp;
                    <RotatingText
                      texts={["slant", "spin", "framing", "angle", "bias"]}
                      rotationInterval={2600}
                      // Stops on the last word instead of cycling forever. The
                      // rotation is worth one pass — it names what the scorer
                      // measures — but it sits directly above the input, and
                      // perpetual movement beside the thing you want someone to
                      // type into competes with it for the whole visit.
                      loop={false}
                      splitBy="characters"
                      staggerDuration={0.02}
                      staggerFrom="last"
                      initial={{ y: "100%" }}
                      animate={{ y: 0 }}
                      exit={{ y: "-120%" }}
                      transition={{ type: "spring", damping: 30, stiffness: 400 }}
                      // Letters travel a full line-height, so each one needs a
                      // window of its own to slide behind. pb keeps descenders
                      // ("p", "g") from being shaved off by that window.
                      mainClassName="inline-flex overflow-hidden"
                      // Each character sits one step further along the lean
                      // axis, so the word crosses from blue to red the way the
                      // gauge does. See index.css for why the axis and not a
                      // colour, and RotatingText for why it is done character
                      // by character rather than as a background gradient.
                      //
                      // The scale is the word's, not the letter's: position is
                      // i/(total-1), so "spin" and "framing" both start at full
                      // blue and land on full red regardless of length. The
                      // one-character guard keeps that division defined.
                      elementStyle={(i, total) => ({
                        color: `color-mix(in oklab, var(--c-lean-end) ${total > 1 ? (i / (total - 1)) * 100 : 50}%, var(--c-lean-start))`,
                      })}
                      splitLevelClassName="overflow-hidden pb-[0.12em]"
                    />
                    &nbsp;in 10 seconds.
                  </span>
                </>
              )}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-dim">
              SlantScan scores political slant, highlights loaded language inline, and shows you the perspectives
              the article left out — for any live URL, on demand.
            </p>
            <div className="mt-8 max-w-2xl"><AnalyzeBox /></div>
          </motion.div>
        </div>
      </section>

      {/* Live wire — renders nothing until ingestion has articles to show */}
      <NewsTicker />

      {/* How it works sits here, ahead of the examples, because it was the
          fifth section on the page: a live example, six recent reports and a
          scrolling wire all arrived before anything said what a slant score is,
          and all three of them assume that vocabulary. The explanation has to
          come before the evidence it lets you read. */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="font-serif text-2xl font-bold text-ink sm:text-3xl">How it works</h2>

        {/* Three cards on wide screens, the stepper on a phone. Not two designs
            for vanity: the three cards are scannable side by side and become
            three full screens of scrolling stacked, while the stepper is one
            card at a time — which is the right shape for a small screen and the
            wrong one for a desktop that can show all four at once.

            The stepper came from the methodology page, where it sat directly
            under two paragraphs telling the same four stages. It belongs in
            front of somebody who has not yet decided to go and read a
            methodology page. */}
        <div className="mt-8 hidden gap-px overflow-hidden rounded-2xl border border-line/10 bg-line/10 md:grid md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-surface p-7">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-ink text-paper"><s.icon size={19} /></span>
              <p className="mt-4 text-xs font-bold uppercase tracking-widest text-faint">Step {i + 1}</p>
              <h3 className="mt-1 font-serif text-xl font-bold text-ink">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-dim">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 md:hidden" data-testid="pipeline-stepper">
          <Stepper
            backButtonText="Back"
            nextButtonText="Next"
            stepCircleContainerClassName="!shadow-none border border-line/10 bg-surface !rounded-2xl"
            contentClassName="text-dim"
            // Wraps back to step one instead of ending. Nothing here is a task
            // to complete — it is four sentences about how the product works —
            // and upstream's "Complete" collapsed the card to nothing with no
            // way back, inside a section still headed "How it works".
            finalButtonText="Start over"
            wrapOnComplete
          >
            {STEPS.map((s, i) => (
              <Step key={s.title}>
                <h3 className="font-serif text-lg font-bold text-ink">{i + 1}. {s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed">{s.body}</p>
              </Step>
            ))}
          </Stepper>
        </div>

        <p className="mt-4 text-sm text-dim">
          <Link to="/methodology" className="font-semibold text-ink underline">
            What the model actually reads, and where the limits are
          </Link>
        </p>
      </section>

      {/* Live demo */}
      {demo && (
        <section className="mx-auto max-w-6xl px-5 py-16">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-faint">Live example</p>
              <h2 className="mt-1 font-serif text-2xl font-bold text-ink sm:text-3xl">A real analyzed report</h2>
            </div>
            <Link to={`/report/${demo.id}`} className="hidden items-center gap-1 text-sm font-semibold text-ink hover:underline sm:flex" data-testid="demo-view-full">
              View full report <ArrowRight size={15} />
            </Link>
          </div>
          <Link to={`/report/${demo.id}`} className="block rounded-2xl border border-line/10 bg-surface p-6 transition-colors hover:border-line/25 sm:p-8" data-testid="demo-report">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-faint">{demo.outlet}</p>
                <h3 className="mt-1 font-serif text-xl font-bold leading-snug text-ink">{demo.headline}</h3>
                <p className="mt-3 text-sm leading-relaxed text-dim">"{demo.analysis.one_line_verdict}"</p>
              </div>
              <div className="flex flex-col justify-center">
                <LeanGauge score={demo.analysis.lean_score} label={demo.analysis.lean_label} confidence={demo.analysis.confidence} />
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Recently analyzed */}
      {recent.length > 0 && (
        <section className="border-y border-line/10 bg-surface">
          <div className="mx-auto max-w-6xl px-5 py-14">
            {/* Named for what it offers rather than for what it is. Three card
                grids in a row all read as the same gesture unless each says
                what makes it different — the wire is stories waiting to be
                scored, and these are the ones already done, which is the only
                thing on the page you can read without spending anything. */}
            <div className="mb-6">
              <h2 className="font-serif text-2xl font-bold text-ink">Already scored — free to read</h2>
              <p className="mt-1 text-sm text-faint">
                Somebody already ran these. Open any of them, highlights and all, without an account.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recent.slice(0, 6).map((r, i) => {
                const s = leanStyle(r.lean_label);
                return (
                  // Staggered rather than six cards landing on one frame, which
                  // reads as the section repainting instead of arriving. A CSS
                  // keyframe, not framer: this is decoration, it never needs to
                  // be interrupted, and CSS keeps it off the main thread while
                  // the rest of the page is still fetching.
                  <Link key={r.id} to={`/report/${r.id}`} data-testid={`recent-${r.id}`}
                    className={`group block ${reduceMotion ? "" : "animate-fade-up"}`}
                    style={reduceMotion ? undefined : { animationDelay: `${i * 55}ms` }}>
                    <SpotlightCard
                      // The padding moves down to PixelCard so the canvas covers
                      // the whole card rather than a rectangle inset from it.
                      shellClassName="rounded-xl border border-line/10 bg-surface"
                      className="h-full transition-colors group-hover:border-line/25"
                      // Ink at 6%: on a white card the spotlight should read as
                      // the paper catching light, not as a glow.
                      spotlightColor="var(--c-spotlight)"
                    >
                      <PixelCard className="h-full" contentClassName="pixel-legible h-full p-5" gap={6} colors={pixelInk(isDark)}>
                        {/* Lifts as the pixels come up, so the headline reads as
                            sitting above the field rather than in it. */}
                        <div className="transition-transform duration-200 ease-out group-hover:-translate-y-0.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-faint">{r.outlet}</span>
                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${s.text} ${s.bg} ${s.border}`}>{r.lean_label}</span>
                          </div>
                          <p className="mt-2 line-clamp-2 font-serif text-base font-bold leading-snug text-ink group-hover:underline">{r.headline}</p>
                        </div>
                      </PixelCard>
                    </SpotlightCard>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {/* pt-16 to match every other content section, which the bare pb-8 did
          not. Without it the heading sat 66px below the band above and the last
          answer sat 128px above the footer -- the section's whole allowance of
          air spent underneath it. The footer already contributes 96px of that
          128 through its own mt-24, so the padding here stays at pb-8 rather
          than growing to py-16, which would push the trailing gap to 160. */}
      <section className="mx-auto max-w-3xl px-5 pt-16 pb-8">
        <h2 className="font-serif text-2xl font-bold text-ink sm:text-3xl">Frequently asked</h2>
        <Accordion type="single" collapsible className="mt-6" data-testid="faq">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-line/10">
              <AccordionTrigger className="text-left font-serif text-lg font-semibold text-ink hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-dim">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </main>
  );
}
